import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  HiOutlineDocument, HiOutlineRefresh, HiOutlineExclamationCircle,
  HiOutlineServer, HiOutlineSearch, HiOutlineDownload,
  HiOutlineTrash, HiOutlineUpload, HiOutlinePlay, HiOutlineStop,
  HiOutlineDatabase, HiOutlineChevronDown, HiOutlineAdjustments,
  HiOutlineClipboardCopy, HiOutlineClock, HiOutlineX
} from 'react-icons/hi';
import { SkeletonBlock } from '../components/Skeleton';

const API_BASE = process.env.REACT_APP_API_URL || '';

const SERVICE_LABELS = {
  'ds5-backend': 'DS5 Backend (API)',
  'ds5-frontend': 'DS5 Frontend (Cloud)',
  'ds5-suivi': 'DS5 Suivi (Web)',
  'ds5-admin': 'DS5 Admin',
};

// Short labels for tab buttons
const TAB_LABELS = {
  'ds5-backend': 'Backend (API)',
  'ds5-frontend': 'Frontend (Cloud)',
  'ds5-suivi': 'Suivi (Web)',
  'ds5-admin': 'Admin Panel',
};

const LEVEL_COLORS = {
  ERROR: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  WARN: { color: '#eab308', bg: 'rgba(234,179,8,0.12)' },
  INFO: { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  DEBUG: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)' },
};

const LEVELS = ['ERROR', 'WARN', 'INFO', 'DEBUG'];

export default function LogsPage({ token, env = 'prod', stagingStatus, onNavigate, role }) {
  const [service, setService] = useState('ds5-backend');
  const [stream, setStream] = useState('out');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lineCount, setLineCount] = useState(200);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [liveMode, setLiveMode] = useState(false);
  const [activeTab, setActiveTab] = useState('live'); // 'live' | 'files' | 'archives'
  const [files, setFiles] = useState([]);
  const [archives, setArchives] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [serverLogLevel, setServerLogLevel] = useState(null);
  const [levelDropdownOpen, setLevelDropdownOpen] = useState(false);
  // Phase 2.1 — Date filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  // Phase 2.3 — Error context
  const [expandedErrorIdx, setExpandedErrorIdx] = useState(null);

  // Load more
  const [visibleCount, setVisibleCount] = useState(200);

  const logEndRef = useRef(null);
  const liveRef = useRef(null);
  const searchTimeout = useRef(null);
  const searchInputRef = useRef(null);
  const logViewerRef = useRef(null);

  const services = env === 'prod'
    ? ['ds5-backend', 'ds5-frontend', 'ds5-suivi', 'ds5-admin']
    : ['ds5-backend', 'ds5-frontend', 'ds5-suivi'];
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const envLabel = env === 'prod' ? 'Production' : 'Staging';
  const isStagingDown = env === 'staging' && stagingStatus === 'down';
  const isAdmin = role === 'admin';

  // ─── Keyboard shortcuts ──────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && activeTab === 'live') {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
      if (e.key === 'Escape') {
        if (showDateFilter) { setShowDateFilter(false); return; }
        if (search) { setSearch(''); return; }
        if (levelDropdownOpen) { setLevelDropdownOpen(false); return; }
      }
      if (e.key === 'l' && !e.ctrlKey && !e.metaKey && activeTab === 'live') {
        const tag = document.activeElement?.tagName;
        if (tag !== 'INPUT' && tag !== 'SELECT' && tag !== 'TEXTAREA') {
          setLiveMode(prev => !prev);
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [activeTab, search, showDateFilter, levelDropdownOpen]);

  // Debounce search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(searchTimeout.current);
  }, [search]);

  // Fetch current server log level
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/logs/level`, { headers });
        if (res.ok) {
          const data = await res.json();
          setServerLogLevel(data.level?.toUpperCase() || null);
        }
      } catch { /* ignore */ }
    })();
  }, [headers, isAdmin]);

  const changeServerLevel = async (newLevel) => {
    try {
      const res = await fetch(`${API_BASE}/api/logs/level`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ level: newLevel.toLowerCase() }),
      });
      if (res.ok) {
        const data = await res.json();
        setServerLogLevel(data.current?.toUpperCase());
        showMsg(`Niveau serveur : ${data.previous} → ${data.current}`);
      } else {
        const d = await res.json();
        showMsg(d.error || 'Erreur changement de niveau', 'error');
      }
    } catch (err) { showMsg(err.message, 'error'); }
    setLevelDropdownOpen(false);
  };

  // Fetch structured logs
  const fetchLogs = useCallback(async () => {
    if (isStagingDown) { setLoading(false); setEntries([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ lines: lineCount, stream });
      if (levelFilter.length > 0) params.set('level', levelFilter.join(','));
      if (categoryFilter) params.set('category', categoryFilter);
      if (debouncedSearch) params.set('search', debouncedSearch);

      const res = await fetch(
        `${API_BASE}/api/logs/${env}/${service}?${params}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setVisibleCount(200);
      }
    } catch (err) {
      console.error('Fetch logs error:', err);
    } finally {
      setLoading(false);
    }
  }, [service, env, stream, lineCount, levelFilter, categoryFilter, debouncedSearch, isStagingDown, headers]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Live mode — auto-refresh every 3s
  useEffect(() => {
    if (liveMode && activeTab === 'live') {
      liveRef.current = setInterval(fetchLogs, 3000);
      return () => clearInterval(liveRef.current);
    }
    return () => { if (liveRef.current) clearInterval(liveRef.current); };
  }, [liveMode, fetchLogs, activeTab]);

  // Auto-scroll on new entries in live mode
  useEffect(() => {
    if (liveMode && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [entries, liveMode]);

  // Fetch log files
  const fetchFiles = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/logs/${env}/${service}/files`, { headers });
      if (res.ok) {
        const data = await res.json();
        setFiles(data.files || []);
      }
    } catch { /* ignore */ }
  }, [env, service, headers]);

  // Fetch S3 archives
  const fetchArchives = useCallback(async () => {
    setArchiveLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/logs/archives/${env}?service=${service}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setArchives(data.archives || []);
      }
    } catch { /* ignore */ }
    setArchiveLoading(false);
  }, [env, service, headers]);

  useEffect(() => {
    if (activeTab === 'files') fetchFiles();
    if (activeTab === 'archives') fetchArchives();
  }, [activeTab, fetchFiles, fetchArchives]);

  // Actions
  const showMsg = (text, type = 'success') => {
    setActionMsg({ text, type });
    setTimeout(() => setActionMsg(null), 4000);
  };

  const handleArchive = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/logs/${env}/${service}/archive`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        showMsg(`Archivé → S3 (${data.sizeMB} Mo)`);
        fetchArchives();
      } else {
        showMsg(data.error || 'Erreur archivage', 'error');
      }
    } catch (err) { showMsg(err.message, 'error'); }
  };

  const handleFlush = async (st = 'out') => {
    if (!window.confirm(`Vider le fichier ${service}-${st}.log sur ${envLabel} ?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/logs/${env}/${service}/flush`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stream: st }),
      });
      if (res.ok) { showMsg(`${service}-${st}.log vidé`); fetchLogs(); }
      else { const d = await res.json(); showMsg(d.error, 'error'); }
    } catch (err) { showMsg(err.message, 'error'); }
  };

  const handleDeleteArchive = async (key) => {
    if (!window.confirm('Supprimer cette archive S3 ?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/logs/archives`, {
        method: 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ key }),
      });
      if (res.ok) { showMsg('Archive supprimée'); fetchArchives(); }
      else { const d = await res.json(); showMsg(d.error, 'error'); }
    } catch (err) { showMsg(err.message, 'error'); }
  };

  // Extract unique categories from current entries
  const categories = useMemo(() => {
    const cats = new Set();
    entries.forEach(e => { if (e.category) cats.add(e.category); });
    return Array.from(cats).sort();
  }, [entries]);

  // Level toggle
  const toggleLevel = (lvl) => {
    setLevelFilter(prev =>
      prev.includes(lvl) ? prev.filter(l => l !== lvl) : [...prev, lvl]
    );
  };

  // Client-side date filter
  const filteredEntries = useMemo(() => {
    if (!dateFrom && !dateTo) return entries;
    return entries.filter(e => {
      if (!e.timestamp) return true;
      const ts = e.timestamp.replace('Z', '');
      if (dateFrom && ts < dateFrom) return false;
      if (dateTo && ts > dateTo) return false;
      return true;
    });
  }, [entries, dateFrom, dateTo]);

  // Level counts from filtered entries
  const levelCounts = useMemo(() => {
    const counts = { ERROR: 0, WARN: 0, INFO: 0, DEBUG: 0 };
    filteredEntries.forEach(e => { if (counts[e.level] !== undefined) counts[e.level]++; });
    return counts;
  }, [filteredEntries]);

  // Visible entries (load more / pagination)
  const displayEntries = useMemo(() => {
    return filteredEntries.slice(-visibleCount);
  }, [filteredEntries, visibleCount]);
  const hasMore = filteredEntries.length > visibleCount;

  // Export: copy / download
  const exportText = useMemo(() => {
    return displayEntries.map(e => e.raw || e.message).join('\n');
  }, [displayEntries]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(exportText);
      showMsg(`${displayEntries.length} lignes copiées`);
    } catch { showMsg('Erreur copie', 'error'); }
  };

  const handleDownload = () => {
    const blob = new Blob([exportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${service}-${stream}-${env}-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
    a.click();
    URL.revokeObjectURL(url);
    showMsg('Fichier téléchargé');
  };

  // Error context ±5 lines
  const getErrorContext = (idx) => {
    const start = Math.max(0, idx - 5);
    const end = Math.min(filteredEntries.length - 1, idx + 5);
    const lines = [];
    for (let i = start; i <= end; i++) {
      lines.push({ ...filteredEntries[i], _isTarget: i === idx, _contextIdx: i });
    }
    return lines;
  };

  // When staging is down
  if (isStagingDown) {
    return (
      <div className="fade-in">
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">
            <HiOutlineDocument className="icon" /> Logs {envLabel}
          </div>
        </div>
        <div className="card" style={{ borderLeft: '4px solid var(--accent-red)', padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <HiOutlineExclamationCircle style={{ color: 'var(--accent-red)', fontSize: 24, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Serveur staging arrêté</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Le serveur staging est actuellement éteint. Les logs ne sont pas disponibles.
              </div>
            </div>
            {onNavigate && (
              <button className="btn btn-primary" onClick={() => onNavigate('staging')}
                style={{ padding: '6px 14px', fontSize: 12, whiteSpace: 'nowrap' }}>
                <HiOutlineServer style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Démarrer le serveur
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Action message */}
      {actionMsg && (
        <div className="card" style={{
          marginBottom: 12, padding: '10px 16px',
          borderLeft: `4px solid ${actionMsg.type === 'error' ? 'var(--accent-red)' : 'var(--accent-green)'}`,
          fontSize: 13,
        }}>
          {actionMsg.text}
        </div>
      )}

      {/* Top bar: title + service selector + controls */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="card-title" style={{ marginRight: 8 }}>
            <HiOutlineDocument className="icon" /> Logs {envLabel}
          </div>

          {/* Service selector */}
          <div className="tabs" style={{ marginBottom: 0, padding: 2 }}>
            {services.map(s => (
              <button key={s} className={`tab ${service === s ? 'active' : ''}`}
                onClick={() => setService(s)} style={{ padding: '5px 12px', fontSize: 12 }}>
                {TAB_LABELS[s] || s}
              </button>
            ))}
          </div>

          {/* Stream toggle */}
          <div className="tabs" style={{ marginBottom: 0, padding: 2 }}>
            <button className={`tab ${stream === 'out' ? 'active' : ''}`}
              onClick={() => setStream('out')} style={{ padding: '5px 10px', fontSize: 11 }}>stdout</button>
            <button className={`tab ${stream === 'err' ? 'active' : ''}`}
              onClick={() => setStream('err')} style={{ padding: '5px 10px', fontSize: 11, color: stream === 'err' ? '#ef4444' : undefined }}>stderr</button>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            {/* Live tail toggle */}
            <button
              className={`btn ${liveMode ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setLiveMode(!liveMode)}
              style={{ padding: '5px 10px', fontSize: 11, gap: 4 }}
              title={liveMode ? 'Arrêter le live (L)' : 'Activer le live tail 3s (L)'}
            >
              {liveMode ? <HiOutlineStop /> : <HiOutlinePlay />}
              {liveMode ? 'Live' : 'Live'}
            </button>

            {/* Hot log level switch (admin only) */}
            {isAdmin && serverLogLevel && (
              <div style={{ position: 'relative' }}>
                <button
                  className="btn btn-outline"
                  onClick={() => setLevelDropdownOpen(!levelDropdownOpen)}
                  style={{ padding: '5px 10px', fontSize: 11, gap: 4, display: 'flex', alignItems: 'center' }}
                  title="Changer le niveau de log serveur (sans restart)"
                >
                  <HiOutlineAdjustments />
                  <span style={{
                    color: LEVEL_COLORS[serverLogLevel]?.color || 'var(--text-muted)',
                    fontWeight: 600,
                  }}>
                    {serverLogLevel}
                  </span>
                  <HiOutlineChevronDown style={{ fontSize: 10 }} />
                </button>
                {levelDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 100,
                    background: 'var(--bg-card)', border: '1px solid var(--border-color)',
                    borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: 130, overflow: 'hidden',
                  }}>
                    <div style={{ padding: '6px 10px', fontSize: 10, color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>
                      Niveau serveur
                    </div>
                    {LEVELS.map(lvl => (
                      <button key={lvl}
                        onClick={() => changeServerLevel(lvl)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '6px 12px', fontSize: 11, border: 'none', cursor: 'pointer',
                          background: serverLogLevel === lvl ? LEVEL_COLORS[lvl].bg : 'transparent',
                          color: serverLogLevel === lvl ? LEVEL_COLORS[lvl].color : 'var(--text-primary)',
                          fontWeight: serverLogLevel === lvl ? 700 : 400,
                        }}
                      >
                        <span style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                          background: LEVEL_COLORS[lvl].color, marginRight: 6,
                        }} />
                        {lvl}
                        {serverLogLevel === lvl && ' ✓'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button className="btn btn-outline" onClick={fetchLogs} disabled={loading}
              style={{ padding: '5px 10px', fontSize: 11 }}>
              <HiOutlineRefresh className={loading ? 'spin' : ''} />
            </button>
          </div>
        </div>

        {/* Filter bar — always visible */}
        <div style={{
            marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-color)',
            display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
          }}>
            {/* Level filters */}
            <div style={{ display: 'flex', gap: 4 }}>
              {LEVELS.map(lvl => (
                <button key={lvl}
                  onClick={() => toggleLevel(lvl)}
                  style={{
                    padding: '3px 8px', fontSize: 10, fontWeight: 600, borderRadius: 4,
                    border: '1px solid',
                    borderColor: levelFilter.includes(lvl) ? LEVEL_COLORS[lvl].color : 'var(--border-color)',
                    background: levelFilter.includes(lvl) ? LEVEL_COLORS[lvl].bg : 'transparent',
                    color: levelFilter.includes(lvl) ? LEVEL_COLORS[lvl].color : 'var(--text-muted)',
                    cursor: 'pointer', transition: 'all 0.15s',
                    opacity: levelCounts[lvl] === 0 ? 0.5 : 1,
                  }}>
                  {lvl}
                  {levelCounts[lvl] > 0 && (
                    <span style={{ marginLeft: 3, fontWeight: 400, fontSize: 9 }}>
                      {levelCounts[lvl]}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Category filter */}
            {categories.length > 0 && (
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
                style={{
                  padding: '4px 8px', fontSize: 11, borderRadius: 4,
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-card)', color: 'var(--text-primary)',
                }}>
                <option value="">Toute catégorie</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}

            {/* Date filter toggle */}
            <button
              className={`btn ${showDateFilter || dateFrom || dateTo ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setShowDateFilter(!showDateFilter)}
              style={{ padding: '3px 8px', fontSize: 10, gap: 3 }}
              title="Filtre par date/heure"
            >
              <HiOutlineClock />
              {(dateFrom || dateTo) ? 'Filtre actif' : 'Date'}
            </button>

            {/* Search */}
            <div style={{ position: 'relative', flex: 1, minWidth: 150 }}>
              <HiOutlineSearch style={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-muted)', fontSize: 13,
              }} />
              <input ref={searchInputRef} type="text" value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher... (Ctrl+F)"
                style={{
                  width: '100%', padding: '5px 28px 5px 26px', fontSize: 11, borderRadius: 4,
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-card)', color: 'var(--text-primary)',
                }} />
              {search && (
                <button onClick={() => setSearch('')} style={{
                  position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                  fontSize: 12, padding: 2, display: 'flex',
                }}>
                  <HiOutlineX />
                </button>
              )}
            </div>

            {/* Lines count */}
            <select value={lineCount} onChange={e => setLineCount(parseInt(e.target.value))}
              style={{
                padding: '4px 8px', fontSize: 11, borderRadius: 4,
                border: '1px solid var(--border-color)',
                background: 'var(--bg-card)', color: 'var(--text-primary)',
              }}>
              <option value={100}>100 lignes</option>
              <option value={200}>200 lignes</option>
              <option value={500}>500 lignes</option>
              <option value={1000}>1000 lignes</option>
            </select>
          </div>

        {/* Date filter row (expandable) */}
        {showDateFilter && (
          <div style={{
            marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border-color)',
            display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 11,
          }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>Depuis :</span>
            <input type="datetime-local" value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              style={{
                padding: '3px 6px', fontSize: 11, borderRadius: 4,
                border: '1px solid var(--border-color)',
                background: 'var(--bg-card)', color: 'var(--text-primary)',
              }} />
            <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>Jusqu'à :</span>
            <input type="datetime-local" value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              style={{
                padding: '3px 6px', fontSize: 11, borderRadius: 4,
                border: '1px solid var(--border-color)',
                background: 'var(--bg-card)', color: 'var(--text-primary)',
              }} />
            {(dateFrom || dateTo) && (
              <button className="btn btn-outline"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                style={{ padding: '3px 8px', fontSize: 10, color: 'var(--accent-red)' }}>
                <HiOutlineX style={{ fontSize: 10 }} /> Effacer
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs: Live | Files | Archives */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab ${activeTab === 'live' ? 'active' : ''}`}
          onClick={() => setActiveTab('live')} style={{ fontSize: 12 }}>
          <HiOutlineDocument style={{ marginRight: 4 }} /> Logs en direct
        </button>
        <button className={`tab ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')} style={{ fontSize: 12 }}>
          <HiOutlineDatabase style={{ marginRight: 4 }} /> Fichiers
        </button>
        <button className={`tab ${activeTab === 'archives' ? 'active' : ''}`}
          onClick={() => setActiveTab('archives')} style={{ fontSize: 12 }}>
          <HiOutlineUpload style={{ marginRight: 4 }} /> Archives S3
        </button>
      </div>

      {/* ── Tab: Live logs ── */}
      {activeTab === 'live' && (
        <div className="card">
          <div className="card-header" style={{ marginBottom: 8 }}>
            <div className="card-title" style={{ fontSize: 13 }}>
              {stream === 'err' && <span style={{ color: 'var(--accent-red)' }}>stderr</span>}
              {stream === 'out' && 'stdout'} — {SERVICE_LABELS[service] || service}
              {liveMode && (
                <span style={{
                  marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 10, color: 'var(--accent-green)', fontWeight: 500,
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: 'var(--accent-green)', animation: 'pulse 1.5s infinite',
                  }} />
                  LIVE
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {/* Level summary badges */}
              <div style={{ display: 'flex', gap: 4, marginRight: 6 }}>
                {LEVELS.map(lvl => levelCounts[lvl] > 0 && (
                  <span key={lvl} style={{
                    fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                    color: LEVEL_COLORS[lvl].color, background: LEVEL_COLORS[lvl].bg,
                  }}>
                    {levelCounts[lvl]} {lvl}
                  </span>
                ))}
              </div>
              <span className={`badge ${env === 'prod' ? 'error' : 'degraded'}`}
                style={{ fontSize: 10, padding: '2px 8px' }}>
                {envLabel}
              </span>
              <span className="badge loading" style={{ fontSize: 10, padding: '2px 8px' }}>
                {displayEntries.length}{hasMore ? `/${filteredEntries.length}` : ''} lignes
              </span>
            </div>
          </div>

          {/* Load more button (at top) */}
          {hasMore && (
            <div style={{ textAlign: 'center', marginBottom: 6 }}>
              <button className="btn btn-outline"
                onClick={() => setVisibleCount(prev => Math.min(prev + 200, filteredEntries.length))}
                style={{ padding: '3px 12px', fontSize: 10 }}>
                Charger {Math.min(200, filteredEntries.length - visibleCount)} lignes précédentes
                ({filteredEntries.length - visibleCount} restantes)
              </button>
            </div>
          )}

          {/* Log viewer */}
          <div ref={logViewerRef} className="log-viewer" style={{ maxHeight: 600, fontSize: 11.5 }}>
            {loading && entries.length === 0 ? (
              <SkeletonBlock width="100%" height={300} borderRadius={6} />
            ) : displayEntries.length === 0 ? (
              <span style={{ color: 'var(--text-muted)' }}>Aucun log disponible</span>
            ) : (
              displayEntries.map((entry, i) => {
                const realIdx = filteredEntries.length - displayEntries.length + i;
                const isErrorExpanded = expandedErrorIdx === realIdx;
                return (
                  <React.Fragment key={i}>
                    <LogLine
                      entry={entry}
                      search={debouncedSearch}
                      isError={entry.level === 'ERROR'}
                      isExpanded={isErrorExpanded}
                      onClick={entry.level === 'ERROR'
                        ? () => setExpandedErrorIdx(isErrorExpanded ? null : realIdx)
                        : undefined}
                    />
                    {/* Error context ±5 */}
                    {isErrorExpanded && (
                      <div style={{
                        margin: '2px 0 6px 0', padding: '4px 0',
                        borderTop: '1px dashed var(--border-color)',
                        borderBottom: '1px dashed var(--border-color)',
                      }}>
                        <div style={{ fontSize: 9, color: 'var(--text-muted)', marginBottom: 2, paddingLeft: 4 }}>
                          Contexte ±5 lignes
                        </div>
                        {getErrorContext(realIdx).map((ctx, ci) => (
                          <div key={ci} className={ctx._isTarget ? 'log-line-error-target' : 'log-line-context'}>
                            <LogLine entry={ctx} search={debouncedSearch} compact />
                          </div>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              })
            )}
            <div ref={logEndRef} />
          </div>

          {/* Action buttons */}
          <div style={{
            marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-color)',
            display: 'flex', gap: 8, flexWrap: 'wrap',
          }}>
            <button className="btn btn-outline" onClick={handleCopy}
              style={{ padding: '4px 10px', fontSize: 11 }}
              title="Copier les logs affichés">
              <HiOutlineClipboardCopy /> Copier
            </button>
            <button className="btn btn-outline" onClick={handleDownload}
              style={{ padding: '4px 10px', fontSize: 11 }}
              title="Télécharger en fichier .log">
              <HiOutlineDownload /> Télécharger .log
            </button>

            {isAdmin && (
              <>
                <div style={{ flex: 1 }} />
                <button className="btn btn-outline" onClick={() => handleFlush(stream)}
                  style={{ padding: '4px 10px', fontSize: 11, color: 'var(--accent-red)' }}>
                  <HiOutlineTrash /> Vider {stream}
                </button>
                <button className="btn btn-outline" onClick={handleArchive}
                  style={{ padding: '4px 10px', fontSize: 11 }}>
                  <HiOutlineUpload /> Archiver S3
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Files ── */}
      {activeTab === 'files' && (
        <div className="card">
          <div className="card-header" style={{ marginBottom: 12 }}>
            <div className="card-title" style={{ fontSize: 13 }}>
              Fichiers de log — {SERVICE_LABELS[service] || service}
            </div>
            <button className="btn btn-outline" onClick={fetchFiles} style={{ padding: '4px 10px', fontSize: 11 }}>
              <HiOutlineRefresh />
            </button>
          </div>

          {files.length === 0 ? (
            <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>
              Aucun fichier trouvé
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Fichier</th>
                    <th>Taille</th>
                    <th>Modifié</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((f, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{f.name}</td>
                      <td>{f.sizeMB} Mo</td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {f.modified ? new Date(f.modified).toLocaleString('fr-FR') : '—'}
                      </td>
                      <td>
                        <span className={`badge ${f.rotated ? 'degraded' : 'healthy'}`}
                          style={{ fontSize: 10, padding: '2px 6px' }}>
                          {f.rotated ? 'rotated' : 'current'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Archives S3 ── */}
      {activeTab === 'archives' && (
        <div className="card">
          <div className="card-header" style={{ marginBottom: 12 }}>
            <div className="card-title" style={{ fontSize: 13 }}>
              Archives S3 — {SERVICE_LABELS[service] || service}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {isAdmin && (
                <button className="btn btn-outline" onClick={handleArchive}
                  style={{ padding: '4px 10px', fontSize: 11 }}>
                  <HiOutlineUpload /> Archiver maintenant
                </button>
              )}
              <button className="btn btn-outline" onClick={fetchArchives}
                style={{ padding: '4px 10px', fontSize: 11 }}>
                <HiOutlineRefresh className={archiveLoading ? 'spin' : ''} />
              </button>
            </div>
          </div>

          {archiveLoading ? (
            <SkeletonBlock width="100%" height={120} borderRadius={6} />
          ) : archives.length === 0 ? (
            <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>
              Aucune archive S3 disponible.
              {!archiveLoading && (
                <span style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                  Le SDK S3 doit être installé (npm i @aws-sdk/client-s3) et le bucket configuré.
                </span>
              )}
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Archive</th>
                    <th>Taille</th>
                    <th>Date</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {archives.map((a, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{a.name}</td>
                      <td>{a.sizeMB} Mo</td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {a.lastModified ? new Date(a.lastModified).toLocaleString('fr-FR') : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <a href={`${API_BASE}/api/logs/archives/download?key=${encodeURIComponent(a.key)}`}
                            className="btn btn-outline"
                            style={{ padding: '3px 8px', fontSize: 10, textDecoration: 'none' }}>
                            <HiOutlineDownload />
                          </a>
                          {isAdmin && (
                            <button className="btn btn-outline" onClick={() => handleDeleteArchive(a.key)}
                              style={{ padding: '3px 8px', fontSize: 10, color: 'var(--accent-red)' }}>
                              <HiOutlineTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {/* Keyboard shortcuts hint */}
      <div style={{ textAlign: 'center', padding: '8px 0', fontSize: 10, color: 'var(--text-muted)' }}>
        <kbd style={{ padding: '1px 4px', borderRadius: 3, border: '1px solid var(--border-color)', fontSize: 9 }}>Ctrl+F</kbd> Rechercher
        {' · '}
        <kbd style={{ padding: '1px 4px', borderRadius: 3, border: '1px solid var(--border-color)', fontSize: 9 }}>L</kbd> Live
        {' · '}
        <kbd style={{ padding: '1px 4px', borderRadius: 3, border: '1px solid var(--border-color)', fontSize: 9 }}>Esc</kbd> Fermer
      </div>
    </div>
  );
}

// ─── Highlight component — wraps matched text in <mark> ───────────

function HighlightText({ text, search }) {
  if (!search || !text) return <>{text}</>;
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === search.toLowerCase()
          ? <mark key={i}>{part}</mark>
          : part
      )}
    </>
  );
}

// ─── LogLine component — renders a single log line with highlight ──

function LogLine({ entry, search, isError, isExpanded, onClick, compact }) {
  const { timestamp, level, category, message } = entry;
  const colors = LEVEL_COLORS[level] || LEVEL_COLORS.INFO;

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', gap: 0, lineHeight: compact ? 1.4 : 1.6, minHeight: compact ? 16 : 20,
        cursor: isError ? 'pointer' : 'default',
        borderRadius: 2,
        padding: isError ? '0 2px' : 0,
      }}
      title={isError ? (isExpanded ? 'Cliquer pour fermer le contexte' : 'Cliquer pour voir ±5 lignes de contexte') : undefined}
    >
      {/* Timestamp */}
      {timestamp && (
        <span style={{
          color: 'var(--text-muted)', whiteSpace: 'nowrap', marginRight: 6,
          flexShrink: 0, fontSize: compact ? 10 : 11,
        }}>
          {timestamp.replace('T', ' ').replace('Z', '')}
        </span>
      )}
      {/* Level badge */}
      <span style={{
        display: 'inline-block', width: compact ? 38 : 44, textAlign: 'center',
        padding: '0 4px', borderRadius: 3, fontSize: compact ? 9 : 10, fontWeight: 700,
        color: colors.color, background: colors.bg,
        marginRight: 4, flexShrink: 0, lineHeight: compact ? '15px' : '18px',
      }}>
        {level}
      </span>
      {/* Category */}
      {category && (
        <span style={{
          color: '#a78bfa', fontSize: compact ? 10 : 11, marginRight: 6, flexShrink: 0,
        }}>
          [{category}]
        </span>
      )}
      {/* Message with highlight */}
      <span style={{ wordBreak: 'break-all' }}>
        <HighlightText text={message} search={search} />
      </span>
    </div>
  );
}
